import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, ShieldCheck, Loader2, KeyRound, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { api } from "../lib/api";
import { toast } from "sonner";
import SmsOtpForm from "../components/auth/SmsOtpForm";
import EmailOtpForm from "../components/auth/EmailOtpForm";

export default function Login() {
  const { loginWithPassword, completeLogin } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const loc = useLocation();
  // Return to the page that sent the user here (e.g. an NFC tag link /t/{code}); internal paths only.
  const from = loc.state?.from;
  const dest = from?.pathname && from.pathname.startsWith("/") && !from.pathname.startsWith("/login")
    ? `${from.pathname}${from.search || ""}` : "/dashboard";
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);
  const [mode, setMode] = useState("password"); // password | sms | email
  const [channels, setChannels] = useState({ sms: null, email: false });

  useEffect(() => {
    api.get("/auth/otp/channels").then((r) => setChannels(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const slug = params.get("org");
    if (slug) {
      api.get(`/tenant/org/by-slug/${slug}`).then((r) => setOrgInfo(r.data)).catch(() => {});
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginWithPassword(email, pw);
      // Remember me: with no expiry vs session-only — JWT already 7d; we just persist token in localStorage either way
      if (!remember) sessionStorage.setItem("f360_session_only", "1");
      toast.success("Welcome back to Fire360Pro");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const finishOtpLogin = (data) => {
    completeLogin(data);
    toast.success("Welcome back to Fire360Pro");
    nav(dest, { replace: true });
  };

  const tabs = [
    ["password", "Password", Lock],
    channels.sms && ["sms", "SMS code", Smartphone],
    channels.email && ["email", "Email code", KeyRound],
  ].filter(Boolean);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden grid-bg px-4 py-10">
      <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-orange-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-pink-500/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="glass-strong rounded-lg p-8 md:p-10 f360-border-gradient" data-testid="login-card">
          <div className="mb-6 text-center">
            <img src="/logo.jpg" alt="Fire360Pro" className="h-16 mx-auto mb-4" />
            {orgInfo ? (
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight">
                  <span className="text-gray-900">{orgInfo.name}</span>
                </h1>
                <p className="text-xs text-gray-500 mt-1">Sign in to {orgInfo.slug}</p>
              </div>
            ) : (
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Fire360Pro</h1>
                <p className="text-xs text-gray-500 mt-1">Maharashtra ACMS Compliant Fire Safety Platform</p>
              </div>
            )}
          </div>

          <div className="mt-8 space-y-4">
            {tabs.length > 1 && (
              <div className="flex gap-1 p-1 rounded-lg bg-gray-100" data-testid="login-mode-tabs">
                {tabs.map(([key, label, Icon]) => (
                  <button key={key} type="button" onClick={() => setMode(key)} data-testid={`login-mode-${key}`}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-semibold transition ${mode === key ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-800"}`}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            )}

            {mode === "sms" && channels.sms && (
              <SmsOtpForm config={channels.sms}
                onVerified={async (idToken) => finishOtpLogin((await api.post("/auth/otp/sms/login", { id_token: idToken })).data)} />
            )}
            {mode === "email" && <EmailOtpForm onSignedIn={finishOtpLogin} />}

            {mode === "password" && <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input type="email" data-testid="login-email-input" placeholder="Email ID" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12 bg-gray-50 border-gray-200 focus-visible:ring-orange-500" required />
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input type="password" data-testid="login-password-input" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} className="pl-10 h-12 bg-gray-50 border-gray-200 focus-visible:ring-orange-500" required />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="remember-me" /> Remember me
                </label>
                <Link to="/forgot-password" className="text-xs text-orange-600 hover:text-orange-700" data-testid="forgot-link">Forgot Password?</Link>
              </div>

              <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full h-12 f360-gradient text-white font-semibold text-base btn-shimmer border-0 hover:opacity-95">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Sign In"}
              </Button>
            </form>}

          

            
          </div>

          <div className="mt-8 flex items-center gap-2 justify-center">
            <ShieldCheck className="w-3.5 h-3.5 text-orange-500" strokeWidth={2.5} />
            <span className="text-[10px] tracking-wider uppercase text-gray-500">GR No. MFS-2023/C.R.370/UD-14 Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
}
